import React, { useMemo, useState } from 'react';
import { PrintRecord, UserRole } from './IReport';
import { ChevronDown, ChevronUp, ArrowUpDown, MoreHorizontal, FileText } from 'lucide-react';
import { StatusBadge } from './StatusBadges';
import { Box, CircularProgress, Typography } from '@mui/material';

interface PrintTableProps {
  data: PrintRecord[];
  isLoading: boolean;
  onRowClick: (record: PrintRecord) => void;
  currentUser: any;
}

type SortField = 'datePrinted' | 'centreName' | 'tutorName' | 'printCount' | 'status';
type SortDirection = 'asc' | 'desc';


const parseDateTime = (dateStr: string): Date => {
  if (!dateStr || dateStr === '') return new Date(0);
  return new Date(dateStr);
};

export const PrintTable: React.FC<PrintTableProps> = ({ data, isLoading, onRowClick, currentUser }) => {
  const [sortField, setSortField] = useState<SortField>('datePrinted');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'datePrinted' ? 'desc' : 'asc');
    }
  };

 const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortField) {
        case 'datePrinted':
          aVal = parseDateTime(a.datePrinted || '').getTime();
          bVal = parseDateTime(b.datePrinted || '').getTime();
          break;

        case 'centreName':
          aVal = (a.centreName || '').trim().toLowerCase();
          bVal = (b.centreName || '').trim().toLowerCase();
          break;

        case 'tutorName':
          aVal = (a.tutorName || '').trim().toLowerCase();
          bVal = (b.tutorName || '').trim().toLowerCase();
          break;

        case 'printCount':
          aVal = a.printCount || 0;
          bVal = b.printCount || 0;
          break;

        default:
          aVal = bVal = 0;
      }

      // Handle empty/missing values (push to bottom)
      if (!aVal && aVal !== 0) aVal = sortDirection === 'asc' ? Infinity : -Infinity;
      if (!bVal && bVal !== 0) bVal = sortDirection === 'asc' ? Infinity : -Infinity;

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDirection]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-slate-400" />;
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 text-blue-600 font-bold" />
      : <ChevronDown className="w-4 h-4 text-blue-600 font-bold" />;
  };

if (isLoading) {
  return (
    <Box sx={{ textAlign: "center", py: 6 }}>
      <CircularProgress thickness={4} color="primary" size={48} />
      <Typography sx={{ mt: 2, color: "text.secondary" }}>
        Loading print history...
      </Typography>
    </Box>
  );
}

  if (data.length === 0) {
    return (
      <div className="p-16 text-center bg-slate-50 rounded-lg border border-dashed border-slate-300 m-4">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-800 mb-1">No print history found</h3>
        <p className="text-slate-500 max-w-sm mx-auto">
          There are no records matching your current filters. Try selecting a different centre, day, or clearing your search.
        </p>
      </div>
    );
  }

  return (
  <div className="h-[100%] flex flex-col">
    <div className="flex-1 overflow-auto custom-scrollbar">
      <table className="w-full text-left border-collapse">
        <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          <tr>
            <th 
                className="cursor-pointer hover:bg-slate-100 transition-colors group"
                onClick={() => handleSort('datePrinted')}
            >
                <div className="flex items-center gap-2">
                    Date Created
                    <SortIcon field="datePrinted" />
                </div>
            </th>
            <th 
                className="cursor-pointer hover:bg-slate-100 transition-colors group"
                onClick={() => handleSort('centreName')}
            >
                 <div className="flex items-center gap-2">
                    Centre
                    <SortIcon field="centreName" />
                </div>
            </th>
            <th >Class Details</th>
             {currentUser.role === UserRole.TUTOR_ADMIN && (
            <th 
                className=" cursor-pointer hover:bg-slate-100 transition-colors group"
                onClick={() => handleSort('tutorName')}
            >
                 <div className="flex items-center gap-2">
                    Tutor
                    <SortIcon field="tutorName" />
                </div>
            </th>
             )}
            <th className="w-1/4">Booklet (Click for details)</th>
            <th >Topic</th>
             <th 
                className="cursor-pointer hover:bg-slate-100 transition-colors group text-center"
                onClick={() => handleSort('printCount')}
            >
                 <div className="flex items-center justify-center gap-2">
                    Count
                    <SortIcon field="printCount" />
                </div>
            </th>
            <th className=" text-center">Status</th>
            {currentUser.role === UserRole.TUTOR_ADMIN && (
                <th className="text-right">Action</th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-100">
          {sortedData.map((record) => (
            <tr 
                key={record.id} 
                onClick={() => onRowClick(record)}
                className="hover:bg-blue-50/50 transition-colors cursor-pointer group"
            >
              <td className="px-5 py-4 text-[13px] text-slate-600 whitespace-nowrap">
                {new Date(record.datePrinted).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                <span className="block text-[12px] text-slate-400 mt-0.5">{new Date(record.datePrinted).toLocaleTimeString('en-AU', {hour: '2-digit', minute:'2-digit'})}</span>
              </td>
              <td className="px-5 py-4 text-[13px]  text-slate-700 whitespace-nowrap font-medium">
                {record.centreName}
              </td>
              <td className="px-5 py-4 text-[13px] text-slate-600">
                 <div className="font-medium text-slate-800">{record.className.split(' ').slice(0, 3).join(' ')}</div>
                 <div className="text-[12px] text-slate-400">{record.dayOfWeek.split(' ').slice(0, 3).join(' ')}</div>
                 {/* <div className="text-[12px] text-slate-400">{record.dayOfWeek} • {record.className.includes('pm') || record.className.includes('am') ? record.className.split(' ').slice(-3).join(' ') : ''}</div> */}
              </td>
               {currentUser.role === UserRole.TUTOR_ADMIN && (
              <td className="px-5 py-4 text-[13px] text-slate-600">
                <div className="flex items-center gap-2">
                    <div className="w-[24px] h-[24px] rounded-full bg-brand-100 text-brand-600 flex items-center justify-center text-[13px] text-[#0284c7] bg-[#e0f2fe] font-bold">
                        {record.tutorName.charAt(0)}
                    </div>
                    {record.tutorName}
                </div>
              </td>
               )}
              <td className="px-5 py-4">
                <div className="text-brand-600 font-semibold text-[13px] text-[#0284c7] group-hover:text-brand-700 transition-colors group-hover:underline decoration-brand-300 underline-offset-2">
                    {record.bookletName}
                </div>
                <div className="text-[12px] text-slate-400 font-mono mt-1 truncate max-w-[200px]" title={record.bookletDrivePath}>
                    {record.bookletDrivePath}
                </div>
              </td>
               <td className="px-5 py-4 text-sm text-slate-600">
                <span className="inline-block px-2 py-1 bg-slate-100 rounded text-[13px] text-slate-600 border border-slate-200">
                    {record.topic}
                </span>
              </td>
              <td className="px-5 py-4 text-[13px] text-slate-800 text-center font-semibold">
                {record.printCount}
              </td>
              <td className="px-5 py-4 text-center">
                {record.status && <StatusBadge status={record.status} />}
              </td>
              {currentUser.role === UserRole.TUTOR_ADMIN && (
                <td className="px-5 py-4 text-right">
                    <button className="p-1.5 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                        <MoreHorizontal className="w-[18px] h-[18px]" />
                    </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
</div>
  );
};